import {
    getApiThirdpartyAuth0Config,
    getThirdpartyV1ChartOfAccounts,
    getThirdpartyV1ChartOfAccountsByCoaId,
    getThirdpartyV1Databases,
    getThirdpartyV1Deals,
    getThirdpartyV1DealsIdByDealIdOperatingStatement,
    getThirdpartyV1DealsByDealIdPeriods,
    getThirdpartyV1DealsNameByDealNameOperatingStatement,
    getThirdpartyV1ProjectsByProjectIdOperatingStatement,
    getThirdpartyV1ProjectsByProjectIdPeriods,
} from "@/generated";
import { handleApiResponseAssertData } from "../odin/routers/handleApiResponseAssertData";

export type TApiThirdParty = ReturnType<typeof createApiThirdParty>;

export type TAllowedOperatingStatementPeriods = 1 | 3 | 12;

export const createApiThirdParty = () => ({
    getApiThirdPartyAuth0Config: () =>
        handleApiResponseAssertData(getApiThirdpartyAuth0Config),
    getDatabases: () => handleApiResponseAssertData(getThirdpartyV1Databases),
    getDeals: async (databaseIds: string) => {
        return handleApiResponseAssertData(() =>
            getThirdpartyV1Deals({
                headers: { "x-company-databases": databaseIds },
            }),
        );
    },

    getDealsByDealIdPeriods: async ({ dealId }: { dealId: number }) =>
        handleApiResponseAssertData(() =>
            getThirdpartyV1DealsByDealIdPeriods({
                path: {
                    deal_id: dealId,
                },
            }),
        ),
    getDealsByProjectIdPeriods: async ({
        projectId,
        databaseId,
    }: {
        projectId: number;
        databaseId: number;
    }) =>
        handleApiResponseAssertData(() =>
            getThirdpartyV1ProjectsByProjectIdPeriods({
                path: {
                    project_id: projectId,
                },
                query: {
                    database_id: databaseId,
                },
            }),
        ),
    getDealsByDealIdOperatingStatement: async ({
        dealId,
        startDate,
        endDate,
        aggregationInterval,
        detailed,
    }: {
        dealId: number;
        startDate?: string;
        endDate?: string;
        aggregationInterval?: TAllowedOperatingStatementPeriods;
        detailed?: boolean;
    }) =>
        handleApiResponseAssertData(() =>
            getThirdpartyV1DealsIdByDealIdOperatingStatement({
                path: {
                    deal_id: dealId,
                },
                query: {
                    from: startDate,
                    to: endDate,
                    detail: detailed === true ? "detailed" : "aggregated",
                    interval: aggregationInterval,
                },
            }),
        ),
    getDealsByDealNameOperatingStatement: async ({
        dealName,
        startDate,
        endDate,
        aggregationInterval,
        detailed,
    }: {
        dealName: string;
        startDate?: string;
        endDate?: string;
        aggregationInterval?: TAllowedOperatingStatementPeriods;
        detailed?: boolean;
    }) =>
        handleApiResponseAssertData(() =>
            getThirdpartyV1DealsNameByDealNameOperatingStatement({
                path: {
                    deal_name: dealName,
                },
                query: {
                    deal_name: dealName,
                    from: startDate,
                    to: endDate,
                    detail: detailed === true ? "detailed" : "aggregated",
                    interval: aggregationInterval,
                },
            }),
        ),
    getDealsByProjectIdOperatingStatement: async ({
        projectId,
        databaseId,
        startDate,
        endDate,
        aggregationInterval,
        detailed,
    }: {
        projectId: number;
        databaseId: number;
        startDate?: string;
        endDate?: string;
        aggregationInterval?: TAllowedOperatingStatementPeriods;
        detailed?: boolean;
    }) =>
        handleApiResponseAssertData(() =>
            getThirdpartyV1ProjectsByProjectIdOperatingStatement({
                path: {
                    project_id: projectId,
                },
                query: {
                    from: startDate,
                    to: endDate,
                    detail: detailed === true ? "detailed" : "aggregated",
                    interval: aggregationInterval,
                    database_id: databaseId,
                },
            }),
        ),
    getChartOfAccounts: async (databaseIds?: string) =>
        handleApiResponseAssertData(() =>
            getThirdpartyV1ChartOfAccounts({
                headers:
                    databaseIds !== undefined && databaseIds !== ""
                        ? { "x-company-databases": databaseIds }
                        : undefined,
            }),
        ),
    getChartOfAccountsById: async ({ coaId }: { coaId: number }) =>
        handleApiResponseAssertData(() =>
            getThirdpartyV1ChartOfAccountsByCoaId({
                path: { coa_id: coaId },
            }),
        ),
});

export type {
    GetApiThirdpartyAuth0ConfigResponse as TResponseGetThirdPartyAuth0Config,
    GetApiThirdpartyAuth0ConfigError as TErrorGetThirdPartyAuth0Config,
    GetThirdpartyV1DatabasesResponse as TResponseGetThirdPartyDatabases,
    GetThirdpartyV1DatabasesError as TErrorGetThirdPartyDatabases,
    GetThirdpartyV1DealsResponse as TResponseGetThirdPartyDeals,
    GetThirdpartyV1DealsError as TErrorGetThirdPartyDeals,
    GetThirdpartyV1DealsIdByDealIdOperatingStatementResponse as TResponseGetThirdPartyDealsByDealIdOperatingStatement,
    GetThirdpartyV1DealsIdByDealIdOperatingStatementError as TErrorGetThirdPartyDealsByDealIdOperatingStatement,
    GetThirdpartyV1DealsByDealIdPeriodsResponse as TResponseGetThirdPartyDealsByDealIdPeriods,
    GetThirdpartyV1DealsByDealIdPeriodsError as TErrorGetThirdPartyDealsByDealIdPeriods,
    GetThirdpartyV1ProjectsByProjectIdOperatingStatementResponse as TResponseGetThirdPartyProjectsByProjectIdOperatingStatement,
    GetThirdpartyV1ProjectsByProjectIdOperatingStatementError as TErrorGetThirdPartyProjectsByProjectIdOperatingStatement,
    GetThirdpartyV1ProjectsByProjectIdPeriodsResponse as TResponseGetThirdPartyProjectsByProjectIdPeriods,
    GetThirdpartyV1ProjectsByProjectIdPeriodsError as TErrorGetThirdPartyProjectsByProjectIdPeriods,
    GetThirdpartyV1DealsIdByDealIdOperatingStatementData as TGetThirdPartyDealsByDealIdOperatingStatementData,
    GetThirdpartyV1DealsNameByDealNameOperatingStatementResponse as TResponseGetThirdPartyDealsByDealNameOperatingStatement,
    GetThirdpartyV1DealsNameByDealNameOperatingStatementError as TErrorGetThirdPartyDealsByDealNameOperatingStatement,
    GetThirdpartyV1DealsNameByDealNameOperatingStatementData as TGetThirdPartyDealsByDealNameOperatingStatementData,
    GetThirdpartyV1ChartOfAccountsResponse as TResponseGetThirdPartyChartOfAccounts,
    GetThirdpartyV1ChartOfAccountsError as TErrorGetThirdPartyChartOfAccounts,
    GetThirdpartyV1ChartOfAccountsByCoaIdResponse as TResponseGetThirdPartyChartOfAccountsById,
    GetThirdpartyV1ChartOfAccountsByCoaIdError as TErrorGetThirdPartyChartOfAccountsById,
} from "@/generated";
